import Swiper, { A11y, Autoplay, Navigation, Pagination, EffectFade } from 'swiper';
import { getBreakpoint } from '../utils/_breakpoints.js';
import { getDataProperty } from '../utils/_domFunctions.js';

class mHomepageSlider extends HTMLDivElement {
    constructor() {
        super();
        this.slider = null;
        this.elemWrapper = this.querySelector('.swiper-wrapper');
        this.animationRequestId = null;
        this.autoplayPercentage = 0;
        // start with autoplay on pause when client ask for reduced motion
        this.isPauseToggled = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.desktopBp = getBreakpoint('md').minWidth;
        if (typeof this.desktopBp === 'string') {
            this.desktopBp = this.desktopBp.replace('px', '');
        }
        this.desktopMql = window.matchMedia(`screen and (min-width: ${this.desktopBp}px)`);

        // Get dataset attributes
        this.sliderSlidesMobile = getDataProperty(this, 'sliderSlidesMobile', 'string', '');
        this.sliderSlidesDesktop = getDataProperty(this, 'sliderSlidesDesktop', 'number', 1);
        // If timer is on, activate autoplay too
        this.sliderNotAutoplay = getDataProperty(this, 'sliderNotAutoplay', 'boolean', false);
        this.sliderDots = getDataProperty(this, 'sliderDots', 'boolean', false);
        this.sliderInfinite = getDataProperty(this, 'sliderInfinite', 'boolean', false);
        this.sliderSpeed = getDataProperty(this, 'sliderSpeed', 'number', 4000);
        this.hideMobileSlider = getDataProperty(this, 'hideMobileSlider', 'boolean', false);
        this.slideToShow = getDataProperty(this, 'sliderSlides', 'string', '');

        // bind this
        this.onScreenChange = this.onScreenChange.bind(this);
    }

    connectedCallback() {
        this.initSlider();
        this.bindEvents();
    }

    disconnectedCallback() {
        if (this.slider) {
            this.slider.destroy();
            this.slider = null;
        }
        this.unbindEvents();
    }

    initSlider() {
        const autoplayOptions = {
            delay: this.sliderSpeed,
            disableOnInteraction: false,
            pauseOnMouseEnter: true
        };
        const paginationOptions = {
            el: '.swiper-pagination',
            type: 'bullets',
            clickable: true
        };

        this.slider = new Swiper(this, {
            modules: [A11y, Autoplay, Navigation, Pagination, EffectFade],
            a11y: {
                prevSlideMessage: this.dataset.a11ySlidePrev,
                nextSlideMessage: this.dataset.a11ySlideNext
            },
            allowTouchMove: true,
            initialSlide: 0,
            fadeEffect: { crossFade: true },
            effect: 'coverflow',
            navigation: {
                enabled: true,
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev'
            },
            slidesPerView: this.sliderSlidesMobile,
            spaceBetween: this.sliderSlidesMobile > 1 ? 4 : 0,
            breakpoints: {
                [this.desktopBp]: {
                    slidesPerView: this.sliderSlidesDesktop,
                    spaceBetween: this.sliderSlidesDesktop > 1 ? 4 : 0,
                    // nav buttons disappear whitout this
                    navigation: { enabled: true }
                }
            },
            loop: this.sliderInfinite,
            speed: 500,
            autoplay: this.sliderNotAutoplay ? false : autoplayOptions,
            pagination: this.sliderDots ? paginationOptions : false
        });

        // Autoplay handling
        this.slider.on('autoplayTimeLeft', (s, timeLeft, percentage) => {
            this.autoplayPercentage = percentage;
        });
        this.updateAutoplayState();

        this.onScreenChange(this.desktopMql);
    }

    bindEvents() {
        // Hide mobile slider handling
        this.desktopMql.addEventListener('change', this.onScreenChange);
    }

    unbindEvents() {
        this.desktopMql.removeEventListener('change', this.onScreenChange);
    }

    updateAutoplayState() {
        if (this.sliderNotAutoplay) return;

        const toggle = this.isPauseToggled;
        if (toggle) {
            // .pause() is unreliable ; many actions trigger a .resume() internally
            this.slider.autoplay.stop();
        } else {
            this.slider.autoplay.start();
        }
    }

    onScreenChange(mql) {
        if (this.hideMobileSlider) {
            const mobile = !mql.matches;

            if (mobile) {
                this.slider.disable();
                this.classList.add('hide-mobile-slider');
            } else {
                this.classList.remove('hide-mobile-slider');
                this.slider.enable();
            }
        }
    }
}

customElements.define('m-homepage-slider', mHomepageSlider, {
    extends: 'div'
});
